import { Component, Input } from '@angular/core';
import { HelperServiceService } from '../../../utils/helper-service.service';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CourseService } from '../../../pages/courses/course.service';
import { Store } from '@ngrx/store';
import { addCourseContent, setCourseType } from '../../../pages/courses/create-courses/store/course.action';
import { selectCourseContent, selectCourseType } from '../../../pages/courses/create-courses/store/course.selector';
import { Observable, take } from 'rxjs';
import { Router } from '@angular/router';
import { CourseContent, Type } from '../../../pages/courses/create-courses/store/course.model';
import { ToasterService } from '../../../providers/toaster/toaster.service';
import { NonNegativeDirective } from "../../../utils/non-negative.directive";
import {
  courseNameAllowedCharsValidator,
  courseNameMinLengthValidator,
  courseNameNoAllSpacesValidator,
  courseNameTrimValidator,
} from '../../../utils/create_course_validators/general/courseNameValidators';
import {
  descriptionAllowedCharsValidator,
  descriptionTrimValidator,
  descriptionNoAllSpacesValidator,
  descriptionMinLengthValidator
} from '../../../utils/create_course_validators/general/descriptionValidators';

import {
  thumbnailFileValidatorSync,
  usefulForValidator
} from '../../../utils/create_course_validators/general/thumbnailAndUsefulForValidators';

import {
  videoPlaybackLimitValidator,
  videoPlaybackTimeValidator,
} from '../../../utils/create_course_validators/general/videoPlaybackValidators';






@Component({
  selector: 'app-general-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NonNegativeDirective],
  templateUrl: './general-form.component.html',
  styleUrl: './general-form.component.css'
})
export class GeneralFormComponent {

  @Input() set callGetData(value: boolean) {
    if (!value) return;
    this.store.select(selectCourseType).pipe(take(1)).subscribe((type) => {
      if (type?.mode === 'edit' && type.id) {
        this.getData(type.id);
      }
    });
  }

  thumbnail: File | null = null;
  thumbnailUrl: string | ArrayBuffer | null = null;
  courseForm!: FormGroup;
  usefulHints: string[] = [];
  usefulHintInput: string = '';
  courseData$!: Observable<CourseContent | null>;
  courseType$!: Observable<Type | null>;
  isreqSending = false;
  constructor(private helperService: HelperServiceService, private fb: FormBuilder,
    private courseApiService: CourseService, private store: Store, private router: Router
    , private toasterService: ToasterService) {
    this.courseType$ = this.store.select(selectCourseType);
    this.courseData$ = this.store.select(selectCourseContent);
  }


  ngOnInit(): void {
    this.courseForm = this.fb.group({
      course_type: ['', Validators.required],
      course_name: ['', [
        Validators.required,
        Validators.maxLength(100),
        courseNameAllowedCharsValidator(),
        courseNameNoAllSpacesValidator(),
        courseNameMinLengthValidator(2),
        courseNameTrimValidator()
      ]],
     description: [
  '',
  [
    Validators.required,
    descriptionAllowedCharsValidator(),
    descriptionTrimValidator(),
    descriptionNoAllSpacesValidator(),
    descriptionMinLengthValidator(10)
  ]
],
      usefull_for_hint: ['', [usefulForValidator()]],
      video_time: ['', [Validators.required, videoPlaybackTimeValidator(1, 9999)]],
      video_limit: ['', [Validators.required, videoPlaybackLimitValidator(1, 100)]],
      thumbnail: [null],
    });
    this.store.select(selectCourseType).pipe(take(1)).subscribe((type) => {
      this.courseForm.get('course_type')?.setValue(type?.type === 'video' ? 1 : type?.type === 'flashcard' ? 2 : type?.type === 'exam' ? 3 : '');
      // console.log(this.courseForm.get('course_type')?.value);
      if (type?.type === 'flashcard') {
        this.courseForm.removeControl('video_time');
        this.courseForm.removeControl('video_limit');
      }

      if (type && type.mode === 'edit') {
        this.courseData$.pipe(take(1)).subscribe(data => {
          if (!data) {
            if (type.id) {
              this.getData(type.id);
            }
          } else {
            this.patchForm(data);
          }
        });
      } else {
        this.courseForm.get('thumbnail')?.setValidators([Validators.required, thumbnailFileValidatorSync({
          recommendedWidth: type?.type === 'flashcard' ? 500 : 1280,
          recommendedHeight: type?.type === 'flashcard' ? 500 : 720,
          label: type?.type === 'flashcard' ? 'flashcard' : 'video'
        })]);
        this.courseForm.get('thumbnail')?.updateValueAndValidity();
      }

    });
  }

  patchForm(course: any): void {
    this.courseForm.patchValue({
      course_type: course?.course_type_id,
      course_name: course?.course_name,
      description: course?.description,
      usefull_for_hint: course?.usefull_for_hint,
      video_time: course?.playback_time,
      video_limit: course?.playback_limit
    });
    this.thumbnailUrl = course?.thumbnail;
    this.thumbnail = null;
    this.usefulHintInput = '';
    if (course?.usefull_for_hint) {
      this.usefulHints = course.usefull_for_hint
        .split(',')
        .map((hint: string) => hint.trim())
        .filter((hint: string) => hint.length > 0);
      this.updateHintFormField();
    }
  }

  getData(id: string): void {
    this.courseApiService.getCourseContent(id).subscribe((res: any) => {
      try {
        if (res.body.status === 'success') {
          this.store.dispatch(addCourseContent({ content: res.body.data }));
          this.courseData$ = this.store.select(selectCourseContent);
          this.courseData$.pipe(take(1)).subscribe((course) => {
            if (course) {
              this.patchForm(course);
            }
          });
        }
      } catch (error) {
        console.log(error);
      }
    });

  }

  onFileSelected(e: Event): void {
    if (!(e.target instanceof HTMLInputElement)) return;
    if (!e.target.files || e.target.files.length === 0) return;
    
    const file = e.target.files[0];
    
    // Validate file type
    const mime = file.type?.toLowerCase?.() ?? '';
    const allowedMimes = ['image/jpeg', 'image/jpg', 'image/png'];
    const isAllowedType = allowedMimes.includes(mime) || mime === 'image/jfif';
    const name = (file.name || '').toLowerCase();
    const hasAllowedExt = name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png');
    
    if (!isAllowedType && !hasAllowedExt) {
      this.courseForm.get('thumbnail')?.setErrors({ ...this.courseForm.get('thumbnail')?.errors, thumbnailType: true });
      this.courseForm.get('thumbnail')?.markAsTouched();
      return;
    }
    
    // Validate file size
    if (file.size > 2 * 1024 * 1024) {
      this.courseForm.get('thumbnail')?.setErrors({ ...this.courseForm.get('thumbnail')?.errors, thumbnailSize: true });
      this.courseForm.get('thumbnail')?.markAsTouched();
      return;
    }
    
    // Validate file is not corrupted
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      
      // Check if image loaded with valid dimensions
      if (img.width === 0 || img.height === 0) {
        this.courseForm.get('thumbnail')?.setErrors({ ...this.courseForm.get('thumbnail')?.errors, thumbnailCorrupted: true });
        this.courseForm.get('thumbnail')?.markAsTouched();
        return;
      }
      
      // File is valid, proceed with helper service
      this.courseType$.pipe(take(1)).subscribe((type) => {
        const isFlashcard = type?.type === 'flashcard';
        const expectedAspect: 'square' | 'video' = isFlashcard ? 'square' : 'video';
        const recommendedWidth = isFlashcard ? 500 : 1280;
        const recommendedHeight = isFlashcard ? 500 : 720;
        const label = isFlashcard ? 'Flashcard thumbnail' : 'Video thumbnail';

        this.helperService
          .getImageWithAspectCheck(e, expectedAspect, {
            recommendedWidth,
            recommendedHeight,
            label,
          })
          .then(({ image, imageUrl }) => {
            if (image) {
              this.thumbnail = image;
              this.courseForm.get('thumbnail')?.setValue(this.thumbnail);
              this.thumbnailUrl = imageUrl;
              // Clear any previous errors
              this.courseForm.get('thumbnail')?.setErrors(null);
            }
          });
      });
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      this.courseForm.get('thumbnail')?.setErrors({ ...this.courseForm.get('thumbnail')?.errors, thumbnailCorrupted: true });
      this.courseForm.get('thumbnail')?.markAsTouched();
    };
    
    // Timeout after 5 seconds
    setTimeout(() => {
      URL.revokeObjectURL(url);
      this.courseForm.get('thumbnail')?.setErrors({ ...this.courseForm.get('thumbnail')?.errors, thumbnailCorrupted: true });
      this.courseForm.get('thumbnail')?.markAsTouched();
    }, 5000);
    
    img.src = url;
  }

  removeUsefulHint(index: number): void {
    this.usefulHints.splice(index, 1);
    this.updateHintFormField();
  }

  addUsefulHint(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const control = this.courseForm.get('usefull_for_hint');
    const value = String(this.usefulHintInput ?? '').trim();

    if (!value) {
      control?.setErrors(null);
      control?.markAsTouched();
      return;
    }

    // Prevent duplicates (case-insensitive) - duplicates should not be added.
    const exists = this.usefulHints.some((h) => h.trim().toLowerCase() === value.toLowerCase());
    if (exists) {
      this.usefulHintInput = '';
      control?.markAsUntouched();
      return;
    }

    // IMPORTANT: run validators on the attempted value immediately.
    // The UI listens to courseForm.get('usefull_for_hint')!.touched + hasError(...)
    control?.setValue(value);
    control?.markAsTouched();
    control?.updateValueAndValidity();

    if (control?.invalid) {
      // Keep input as-is so user can correct it.
      return;
    }

    this.usefulHints.push(value);

    // Update form control with hints list, clear input.
    this.updateHintFormField();
    this.usefulHintInput = '';
    control?.markAsUntouched();
  }


  updateHintFormField(): void {
    const control = this.courseForm.get('usefull_for_hint');
    control?.setValue(this.usefulHints.join(','));
    control?.updateValueAndValidity({ emitEvent: false });
  }

  onSubmit() {
    const usefulForControl = this.courseForm.get('usefull_for_hint');
    if (this.usefulHints.length > 0) {
      this.updateHintFormField();
    } else {
      usefulForControl?.setValue('');
      usefulForControl?.updateValueAndValidity({ emitEvent: false });
    }

    if (!this.courseForm.valid) {
      this.courseForm.markAllAsTouched();
      this.toasterService.setToaster('error', 'Form Error', 'Please fill all required fields');
      return
    }
    this.isreqSending = true;
    const form = this.courseForm.value;
    const formData = new FormData();
    formData.append('course_type', form.course_type);
    formData.append('course_name', form.course_name);
    formData.append('description', form.description);
    formData.append('thumbnail', form.thumbnail ?? '');
    formData.append('usefull_for_hint', form.usefull_for_hint ?? '');
    formData.append('playback_time', form.video_time ?? '');
    formData.append('playback_limit', form.video_limit ?? '');


    console.log(formData);
    this.store.select(selectCourseType).pipe(take(1)).subscribe((type) => {
      console.log(type);
      if (type && type.mode == 'new') {
        this.courseApiService.createCourse(formData).subscribe((res: any) => {
          try {

            // console.log(res);

            if (res.body.status === 'success') {
              const updatedType = { ...type, id: res.body.data.id, mode: 'edit' as 'edit' | 'new' };
              this.store.dispatch(setCourseType({ courseType: updatedType }));
              this.router.navigate(
                ['/edit-courses', type.type, res.body.data.id],
                { queryParams: { tab: 'structure' } }
              );
              this.toasterService.setToaster('success', 'Course Added', res.body.message ?? 'Course added successfully');
            }
          } catch (error) {

          }
          this.isreqSending = false;
        });

      } else if (type && type.mode == 'edit') {
        this.courseApiService.updateCourseContent(type.id, formData).subscribe((res: any) => {
          try {
            console.log(res);
            if (res.body.status === 'success') {
              if (type.id) {
                this.getData(type.id);
                this.toasterService.setToaster('success', 'Course Added', res.body.message ?? 'Course updated successfully');
              }
            }
          } catch (error) {

          }
          this.isreqSending = false;
        });
      }
    });
  }

  onPlaybackInput(controlName: 'video_time' | 'video_limit', event: Event): void {
    const input = event.target as HTMLInputElement;
    const digitsOnly = (input.value || '').replace(/\D/g, '');

    // Keep the form control value as a raw digit string to avoid scientific notation/precision loss.
    input.value = digitsOnly;
    this.courseForm.get(controlName)?.setValue(digitsOnly, { emitEvent: false });
  }

  private maxDigitsValidator(maxDigits: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      console.log('Validating value:', value);
      if (value === null || value === undefined || value === '') {
        return null;
      }

      const digits = String(value).replace(/\D/g, '');
      console.log('Digits extracted:', digits);
      if (digits.length > maxDigits) {
        return { maxDigits: { requiredLength: maxDigits, actualLength: digits.length } };
      }
      return null;
    };
  }

  private maxHintLengthValidator(maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;
      const hints = String(value)
        .split(',')
        .map((hint) => hint.trim())
        .filter((hint) => hint.length > 0);
      const tooLong = hints.find((hint) => hint.length > maxLength);
      if (tooLong) {
        return { maxHintLength: { requiredLength: maxLength, actualLength: tooLong.length } };
      }
      return null;
    };
  }

  private maxTotalHintLengthValidator(maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;
      const totalLength = String(value).length;
      if (totalLength > maxLength) {
        return { maxTotalLength: { requiredLength: maxLength, actualLength: totalLength } };
      }
      return null;
    };
  }
}

